/**
 * Embedded Auth Bar UI
 * Renders a floating auth bar that matches the portal's NarcityHeader styling
 */

const FALLBACK_AVATAR = 'https://avatar.vercel.sh/milla?s=64&background=111&color=fff';
const LOADING_TIMEOUT_MS = 1000;
const PROFILE_CACHE_KEY = '__milla_profile_cache__';
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const privacyControls = require('./lib/identity/privacyControls');

// Detect if browser's primary language is French
function isFrenchBrowser() {
  if (typeof navigator === 'undefined') return false;
  const primaryLang = navigator.language || navigator.userLanguage || '';
  return primaryLang.toLowerCase().startsWith('fr');
}

// Translations
const TRANSLATIONS = {
  en: {
    logIn: 'Log in',
    subscribe: 'Subscribe',
    syncingSession: 'Syncing session…',
    saveArticle: 'Save article',
    saved: 'Saved',
    accountSettings: 'Account settings',
    scrollToTop: 'Scroll to top'
  },
  fr: {
    logIn: 'Connexion',
    subscribe: "S'abonner",
    syncingSession: 'Synchronisation…',
    saveArticle: 'Sauvegarder',
    saved: 'Sauvegardé',
    accountSettings: 'Paramètres du compte',
    scrollToTop: 'Haut de page'
  }
};

function getTranslations() {
  return isFrenchBrowser() ? TRANSLATIONS.fr : TRANSLATIONS.en;
}

const STYLES = `
  #milla-auth-bar {
    position: fixed;
    top: 12px;
    right: 24px;
    z-index: 9;
    background: rgba(255, 255, 255, 0.98);
    padding: 10px 18px;
    border-radius: 9999px;
    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.18);
    border: 1px solid rgba(15, 23, 42, 0.08);
    backdrop-filter: blur(8px);
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    line-height: 1.5;
    color: #111827;
    text-align: left;
    text-transform: none;
    text-decoration: none;
    letter-spacing: normal;
    word-spacing: normal;
    white-space: normal;
    display: flex;
    align-items: center;
    gap: 14px;
    box-sizing: border-box;
  }
  #milla-auth-bar *, #milla-auth-bar *::before, #milla-auth-bar *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    border: 0;
    font-family: inherit;
    font-style: normal;
    vertical-align: baseline;
    text-decoration: none;
    background: transparent;
  }
  #milla-auth-bar .milla-auth-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4b5563;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.25;
  }
  #milla-auth-bar .milla-auth-loading-dot {
    display: inline-flex;
    width: 8px;
    height: 8px;
    background: #4b5563;
    border-radius: 9999px;
    animation: milla-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  @keyframes milla-ping {
    75%, 100% { transform: scale(2); opacity: 0; }
  }
  #milla-auth-bar .milla-auth-user {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: inherit;
    padding: 4px 8px;
    border-radius: 9999px;
    border: 1px solid transparent;
    transition: border-color 150ms;
    cursor: pointer;
    line-height: 1.5;
    background: transparent;
    text-align: left;
  }
  #milla-auth-bar .milla-auth-user:hover {
    border-color: #e5e7eb;
    text-decoration: none;
  }
  #milla-auth-bar .milla-auth-user:visited,
  #milla-auth-bar .milla-auth-user:active {
    color: inherit;
    text-decoration: none;
  }
  #milla-auth-bar .milla-auth-avatar-wrap {
    position: relative;
    display: block;
    flex-shrink: 0;
  }
  #milla-auth-bar .milla-auth-avatar {
    display: block;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    border: 1px solid #e5e7eb;
    object-fit: cover;
  }
  #milla-auth-bar .milla-auth-pro-badge-avatar {
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    background: #f59e0b;
    color: #ffffff;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 2px #ffffff;
    text-transform: uppercase;
  }
  #milla-auth-bar .milla-auth-user-info {
    display: block;
  }
  #milla-auth-bar .milla-auth-welcome {
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.33;
  }
  #milla-auth-bar .milla-auth-welcome-text {
    font-size: 12px;
    font-weight: 400;
    text-transform: uppercase;
    color: #6b7280;
    line-height: 1.33;
  }
  #milla-auth-bar .milla-auth-pro-badge {
    display: inline-block;
    background: #fef3c7;
    color: #b45309;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    line-height: 1;
  }
  #milla-auth-bar .milla-auth-name {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    line-height: 1.25;
    text-decoration: none;
  }
  #milla-auth-bar .milla-auth-user:hover .milla-auth-name {
    text-decoration: underline;
  }
  #milla-auth-bar .milla-auth-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-style: normal;
    text-decoration: none;
    text-align: center;
    white-space: nowrap;
    transition: all 150ms;
    line-height: 1.25;
    -webkit-appearance: none;
    appearance: none;
    outline: none;
    background: transparent;
  }
  #milla-auth-bar .milla-auth-btn:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
  #milla-auth-bar .milla-auth-btn-subscribe {
    background: #2563eb;
    color: #ffffff;
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
  }
  #milla-auth-bar .milla-auth-btn-subscribe:hover {
    background: #1d4ed8;
    color: #ffffff;
  }
  #milla-auth-bar .milla-auth-btn-login {
    background: transparent;
    color: #111827;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  #milla-auth-bar .milla-auth-btn-login:hover {
    color: #4b5563;
    background: transparent;
  }
  #milla-auth-bar .milla-auth-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  #milla-auth-bar .milla-auth-btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50%;
    background: transparent;
    color: #6b7280;
    transition: color 0.15s ease, background 0.15s ease;
  }
  #milla-auth-bar .milla-auth-btn-icon:hover {
    color: #111827;
    background: #f3f4f6;
  }
  #milla-auth-bar .milla-auth-action-icon {
    display: block;
    width: 18px;
    height: 18px;
  }
  #milla-auth-bar .milla-auth-btn-save[data-state="saved"] {
    color: #111827;
  }
  #milla-auth-bar .milla-auth-btn-summarize {
    background: #2563eb;
    color: #ffffff;
    padding: 6px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
  }
  #milla-auth-bar .milla-auth-btn-summarize:hover {
    background: #1d4ed8;
    color: #ffffff;
  }
  #milla-auth-bar .milla-auth-btn-summarize[disabled] {
    opacity: 0.6;
    cursor: default;
  }
  #milla-auth-bar .milla-auth-summary {
    max-width: 320px;
    margin-left: 8px;
    padding-left: 12px;
    border-left: 1px solid #e5e7eb;
    font-size: 12px;
    color: #4b5563;
  }
  #milla-auth-bar .milla-auth-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    vertical-align: middle;
    fill: currentColor;
  }
  #milla-auth-bar .milla-auth-brand-logo-wrap {
    display: none;
    align-items: center;
    opacity: 0;
    transform: translateX(-20px) scale(0.8);
    transition: opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    pointer-events: none;
  }
  #milla-auth-bar .milla-auth-brand-logo-wrap[data-visible="true"] {
    opacity: 1;
    transform: translateX(0) scale(1);
    pointer-events: auto;
  }
  #milla-auth-bar .milla-auth-brand-logo-wrap a {
    display: flex;
    align-items: center;
  }
  #milla-auth-bar .milla-auth-brand-logo {
    display: block;
    height: 24px;
    width: auto;
  }
  #milla-auth-bar .milla-auth-divider {
    display: none;
    width: 1px;
    height: 28px;
    background: linear-gradient(to bottom, transparent, #e5e7eb, transparent);
    margin: 0 4px;
    opacity: 0;
    transition: opacity 0.3s ease 0.1s;
  }
  #milla-auth-bar .milla-auth-divider[data-visible="true"] {
    opacity: 1;
  }
  #milla-auth-bar .milla-auth-btn-scroll-top {
    display: none;
    align-items: center;
    justify-content: center;
    padding: 4px;
    background: transparent;
    color: #9ca3af;
    opacity: 0;
    transform: translateX(-4px);
    transition: opacity 0.25s ease, transform 0.25s ease, color 0.15s ease;
    pointer-events: none;
    flex-shrink: 0;
  }
  #milla-auth-bar .milla-auth-btn-scroll-top[data-visible="true"] {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
  #milla-auth-bar .milla-auth-btn-scroll-top:hover {
    color: #FF2634;
  }
  #milla-auth-bar .milla-auth-scroll-icon {
    display: block;
    width: 12px;
    height: 12px;
  }
  @keyframes milla-logo-pulse {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.15); }
  }
  #milla-auth-bar .milla-auth-brand-logo-wrap[data-visible="true"] .milla-auth-brand-logo {
    animation: milla-logo-pulse 2s ease-in-out infinite;
    animation-delay: 0.4s;
  }
  #milla-auth-bar .milla-auth-content {
    display: flex;
    align-items: center;
  }
  @media (max-width: 768px) {
    #milla-auth-bar {
      top: 0;
      right: 0;
      left: 0;
      transform: none;
      padding: 8px 12px;
      border-radius: 0;
      box-shadow: none;
      border: none;
      gap: 8px;
      width: 100%;
      max-width: 100%;
      justify-content: space-between;
      background: transparent;
      backdrop-filter: none;
      transition: box-shadow 0.3s ease, border 0.3s ease, background 0.3s ease, backdrop-filter 0.3s ease;
    }
    #milla-auth-bar[data-scrolled="true"] {
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.1);
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(8px);
    }
    #milla-auth-bar .milla-auth-brand-logo-wrap {
      display: flex;
      flex-shrink: 1;
      min-width: 0;
    }
    #milla-auth-bar .milla-auth-brand-logo-wrap a {
      min-width: 0;
    }
    #milla-auth-bar .milla-auth-brand-logo-wrap svg,
    #milla-auth-bar .milla-auth-brand-logo-wrap img {
      max-width: 100%;
    }
    #milla-auth-bar .milla-auth-divider {
      display: block;
    }
    #milla-auth-bar .milla-auth-btn-scroll-top {
      display: flex;
    }
    #milla-auth-bar .milla-auth-user {
      gap: 8px;
      padding: 2px 4px;
      flex-shrink: 0;
    }
    #milla-auth-bar .milla-auth-actions {
      flex-shrink: 0;
    }
    #milla-auth-bar .milla-auth-content {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
      margin-left: auto;
    }
    #milla-auth-bar .milla-auth-btn-login { padding: 8px; }
    #milla-auth-bar .milla-auth-btn-subscribe { padding: 8px 12px; }
    #milla-auth-bar .milla-auth-name-first {
      max-width: 72px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* MTL Blog brand theme - black bar */
  #milla-auth-bar[data-brand="mtlblog"] {
    background: #000;
    border-color: rgba(255, 255, 255, 0.1);
    color: #fff;
    top: 5px;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-login {
    color: #fff;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-login:hover {
    color: #d1d5db;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-name {
    color: #fff;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-welcome-text {
    color: #9ca3af;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-icon {
    color: #9ca3af;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-icon:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-save[data-state="saved"] {
    color: #fff;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-divider {
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-scroll-top {
    color: #9ca3af;
  }
  #milla-auth-bar[data-brand="mtlblog"] .milla-auth-btn-scroll-top:hover {
    color: #fff;
  }
  @media (max-width: 768px) {
    #milla-auth-bar[data-brand="mtlblog"] {
      background: #000;
      top: 0;
    }
  }
`;

const USER_ICON_SVG = `<svg class="milla-auth-icon" fill="currentColor" viewBox="0 0 20 20">
  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
</svg>`;

const PRIVACY_ICON_SVG = `<svg class="milla-auth-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M12 3 4.5 6v5.5c0 4.3 3 7.8 7.5 9.5 4.5-1.7 7.5-5.2 7.5-9.5V6L12 3Z"/>
  <rect x="9" y="10.5" width="6" height="5" rx="1"/><path d="M10 10.5V9a2 2 0 0 1 4 0v1.5"/>
</svg>`;

// Detect current brand based on hostname
function detectBrand() {
  if (typeof window === 'undefined') return 'narcity';
  const hostname = (window.location.hostname || '').toLowerCase();
  if (hostname.includes('mtlblog.com')) return 'mtlblog';
  return 'narcity';
}

const NARCITY_LOGO_SVG = `<svg class="milla-auth-brand-logo" width="120" height="32" viewBox="0 0 127 38" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M15.6881 0.2838C15.8808 0.2838 16.0366 0.433554 16.0366 0.617604V37.3607C16.0366 37.5447 15.8808 37.6942 15.6881 37.6942H12.0183C11.871 37.6942 11.7392 37.6052 11.69 37.4722C10.5983 34.5248 9.43172 31.2092 8.22248 27.617C7.64082 25.8469 7.19173 24.4651 6.88751 23.5074C6.59122 22.5772 6.32143 21.7407 6.07871 20.9993L5.82506 20.0872V37.3607C5.82506 37.5447 5.66926 37.6942 5.47683 37.6942H0.348229C0.156074 37.6942 0 37.5447 0 37.3607V0.617604C0 0.433554 0.156074 0.2838 0.348229 0.2838H3.92729C4.07571 0.2838 4.208 0.374385 4.25666 0.509216C5.74224 4.64917 6.90883 7.98617 7.72337 10.4278C8.12435 11.6224 8.51577 12.8055 8.88669 13.9454C9.24613 15.0492 9.59135 16.1386 9.91389 17.1811L10.2113 18.2472V0.617604C10.2113 0.433554 10.3676 0.2838 10.5598 0.2838H15.6881ZM51.0285 13.7603V7.27641C51.0285 6.73734 50.8582 6.34411 50.5086 6.07367C50.1407 5.78934 49.5891 5.64535 48.8694 5.64535H47.9187V15.3854C49.4237 15.3448 50.1032 15.1495 50.4097 14.9864C50.8262 14.7641 51.0285 14.363 51.0285 13.7603ZM49.667 0.2838C52.1303 0.2838 54.0469 1.00115 55.3633 2.41517C56.6704 3.81898 57.3327 5.85793 57.3327 8.47705V11.796C57.3327 13.6841 57.0039 15.1434 56.3558 16.1318C55.8192 16.9504 54.9785 17.5696 53.8504 17.9793C54.5146 18.2569 55.0782 18.5831 55.5336 18.953C56.1828 19.48 56.6453 20.1688 56.9074 20.9993C57.1594 21.7991 57.2868 22.8092 57.2868 23.9993V37.3607C57.2868 37.5447 57.1304 37.6942 56.9383 37.6942H51.3311C51.1389 37.6942 50.9826 37.5447 50.9826 37.3607V22.9736C50.9826 22.2128 50.7653 21.6282 50.3178 21.1865C49.8726 20.7477 49.2944 20.5346 48.5507 20.5346H47.9187V37.3607C47.9187 37.5447 47.7629 37.6942 47.5705 37.6942H42.0543C41.8622 37.6942 41.7061 37.5447 41.7061 37.3607V0.617604C41.7061 0.433554 41.8622 0.2838 42.0543 0.2838H49.667ZM73.9709 0.947742C75.1648 1.58027 76.0969 2.48115 76.7417 3.62498C77.3824 4.76018 77.7069 6.10639 77.7069 7.62566V11.5119C77.7069 11.696 77.5505 11.8452 77.3584 11.8452H71.7741C71.582 11.8452 71.4256 11.696 71.4256 11.5119V8.43359C71.4256 8.41213 71.4283 8.39066 71.4324 8.36971C71.4502 8.28279 71.471 8.1134 71.471 7.80054C71.471 5.23038 70.361 5.23038 69.9958 5.23038C69.3034 5.23038 68.815 5.43459 68.5017 5.85479C68.1664 6.30432 67.9966 7.05387 67.9966 8.08434V30.5278C67.9966 31.1914 68.1754 31.7507 68.5288 32.19C68.8647 32.6099 69.2698 32.8133 69.7678 32.8133C70.259 32.8133 70.6455 32.6481 70.9511 32.3075C71.2709 31.9512 71.4256 31.5119 71.4256 30.9639V25.4186C71.4256 25.2343 71.582 25.0848 71.7741 25.0848H77.3584C77.5505 25.0848 77.7069 25.2343 77.7069 25.4186V30.0691C77.7069 31.2373 77.5235 32.3183 77.1618 33.2817C76.7972 34.2546 76.2453 35.1062 75.5218 35.815C74.798 36.5226 73.9247 37.0703 72.9262 37.4426C71.9332 37.8128 70.8319 38 69.6541 38C68.1065 38 66.7229 37.6494 65.5404 36.9577C64.3563 36.2655 63.4262 35.2636 62.7764 33.9807C62.1327 32.7115 61.8067 31.2189 61.8067 29.5449V8.9792C61.8067 7.13529 62.1278 5.52413 62.7617 4.19101C63.4027 2.84113 64.3451 1.79286 65.5628 1.07577C66.7756 0.36208 68.2363 0 69.9045 0C71.4166 0 72.7849 0.319405 73.9709 0.947742ZM89.0254 0.2838C89.2181 0.2838 89.3739 0.433554 89.3739 0.617604V37.3607C89.3739 37.5447 89.2181 37.6942 89.0254 37.6942H83.4641C83.2714 37.6942 83.1156 37.5447 83.1156 37.3607V0.617604C83.1156 0.433554 83.2714 0.2838 83.4641 0.2838H89.0254ZM107.485 0.2838C107.677 0.2838 107.834 0.433554 107.834 0.617604V5.31128C107.834 5.49586 107.677 5.64535 107.485 5.64535H104.004V37.3607C104.004 37.5447 103.848 37.6942 103.656 37.6942H98.049C97.8563 37.6942 97.7005 37.5447 97.7005 37.3607V5.64535H94.3109C94.1188 5.64535 93.9624 5.49586 93.9624 5.31128V0.617604C93.9624 0.433554 94.1188 0.2838 94.3109 0.2838H107.485ZM126.924 0.409991C126.991 0.490104 127.015 0.59378 126.991 0.693266L122.396 19.6939V37.3607C122.396 37.5447 122.24 37.6942 122.048 37.6942H116.486C116.294 37.6942 116.137 37.5447 116.137 37.3607V19.8683L111.474 0.693266C111.45 0.594303 111.474 0.490104 111.54 0.409991C111.606 0.330402 111.707 0.2838 111.813 0.2838H117.329C117.505 0.2838 117.653 0.408943 117.675 0.575714L119.286 12.7911L120.858 0.577023C120.88 0.409467 121.028 0.2838 121.204 0.2838H126.652C126.758 0.2838 126.858 0.330402 126.924 0.409991ZM27.3532 27.5523H29.6769L28.5136 16.0899L27.3532 27.5523ZM31.4883 0.750077L37.2111 37.1083C37.2341 37.2549 37.1892 37.4041 37.0878 37.5167C36.9867 37.6295 36.8394 37.6942 36.6841 37.6942H31.3415C31.0687 37.6942 30.8402 37.4968 30.8118 37.2368L30.3474 32.962H26.7678L26.2826 37.2392C26.2528 37.4979 26.0246 37.6942 25.7529 37.6942H20.3427C20.1878 37.6942 20.0399 37.6293 19.9387 37.5162C19.8373 37.4031 19.7928 37.2533 19.8163 37.1065L25.6736 0.748507C25.7138 0.499529 25.9371 0.316002 26.2 0.316002H30.9615C31.225 0.316002 31.4489 0.500575 31.4883 0.750077Z"
    fill="#FF2634"
  />
</svg>`;

const MTLBLOG_LOGO_SVG = `<img src="https://assets.rbl.ms/27256274/origin.png" alt="MTL Blog" width="46" height="34">`;

const MOBILE_SCROLL_THRESHOLD = 300;

let barState = {
  mounted: false,
  container: null,
  user: null,
  status: 'loading', // loading, authenticated, unauthenticated
  auth: null,
  isSaved: false,
  saveLoading: false,
  summarizeLoading: false,
  summaryText: '',
  profileVersion: 0,
  profileRequest: null,
  profileAwaitingToken: false,
  profileToken: null,
  scrolledPastThreshold: false,
  scrollHandler: null
};

const { buildArticlePayload } = require('./embed');

// Article payload for the save control — shared with the embed layer so the
// bar and the history trigger describe the same article.
function buildArticlePayloadForHost() {
  return buildArticlePayload();
}

function buildArticleTextForHost() {
  if (typeof document === 'undefined') return '';
  let root =
    document.querySelector('article') ||
    document.querySelector('.article-body') ||
    document.body;
  if (!root) return '';
  let text = root.innerText || '';
  // Trim extremely long articles
  const MAX = 6000;
  if (text.length > MAX) {
    text = text.slice(0, MAX);
  }
  return text.trim();
}

function injectStyles() {
  if (document.getElementById('milla-auth-bar-styles')) return;
  const style = document.createElement('style');
  style.id = 'milla-auth-bar-styles';
  style.textContent = STYLES;
  document.head.appendChild(style);
}

function buildProfileUrl(auth) {
  const cfg = auth?.getConfig?.();
  const base = cfg?.issuer || window.location.origin;
  return `${base.replace(/\/$/, '')}/me`;
}

function mapProfile(raw = {}) {
  const name = raw.displayName || raw.name || raw.fullName || raw.username || raw.email || 'Milla Member';
  const firstName = name.split(' ')[0] || name;
  const avatarUrl = raw.avatarUrl || raw.profileImageUrl || raw.photoUrl ||
    `https://avatar.vercel.sh/${encodeURIComponent(name)}?size=64`;
  const proStatus = raw.planStatus ?? raw.proStatus ?? null;
  return {
    name,
    firstName,
    email: raw.email || '',
    avatarUrl: avatarUrl || FALLBACK_AVATAR,
    proStatus
  };
}

function getCachedProfile() {
  try {
    const cached = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) return null;
    const { profile, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > PROFILE_CACHE_TTL_MS) {
      sessionStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }
    return profile;
  } catch {
    return null;
  }
}

function setCachedProfile(profile) {
  try {
    sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
      profile,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore storage errors
  }
}

function clearCachedProfile() {
  try {
    sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
}

async function fetchProfile(auth) {
  const profileUrl = buildProfileUrl(auth);
  const response = await auth.fetch(profileUrl, { credentials: 'include', cache: 'no-store' });
  if (!response.ok) {
    const error = new Error('Unable to fetch profile');
    error.status = response.status;
    throw error;
  }
  const data = await response.json();
  // /me reads the current member record, including webhook-synced Pro status.
  return mapProfile(data);
}

function renderLoading() {
  const t = getTranslations();
  return `
    <div class="milla-auth-loading">
      <span class="milla-auth-loading-dot"></span>
      <span>${t.syncingSession}</span>
    </div>
  `;
}

function buildNewsletterUrl(auth) {
  const cfg = auth?.getConfig?.();
  const base = cfg?.issuer || window.location.origin;
  return `${base.replace(/\/$/, '')}/newsletter`;
}

function renderLoggedOut() {
  const t = getTranslations();
  const newsletterUrl = buildNewsletterUrl(barState.auth);
  return `
    <button class="milla-auth-btn milla-auth-btn-login" data-action="login">
      ${USER_ICON_SVG}
      ${t.logIn}
    </button>
    ${renderPrivacyButton()}
    <a href="${newsletterUrl}" class="milla-auth-btn milla-auth-btn-subscribe">
      ${t.subscribe}
    </a>
  `;
}

function renderLoggedIn(user, state) {
  const isPro = user.proStatus === 'ACTIVE';
  const cfg = barState.auth?.getConfig?.();
  const accountUrl = cfg?.issuer ? `${cfg.issuer.replace(/\/$/, '')}/account` : '/account';
  const t = getTranslations();

  return `
    <a href="${accountUrl}" class="milla-auth-user">
      <div class="milla-auth-avatar-wrap">
        <img class="milla-auth-avatar" src="${user.avatarUrl || FALLBACK_AVATAR}" alt="${user.name}" />
        ${isPro ? '<span class="milla-auth-pro-badge-avatar">PRO</span>' : ''}
      </div>
      <div class="milla-auth-user-info">
        <div class="milla-auth-welcome">
          ${isPro ? '<span class="milla-auth-pro-badge">Pro</span>' : ''}
        </div>
        <span class="milla-auth-name milla-auth-name-first">${user.firstName}</span>
      </div>
    </a>
    <div class="milla-auth-actions">
      ${renderPrivacyButton()}
      <button
        class="milla-auth-btn milla-auth-btn-icon milla-auth-btn-save"
        data-action="save"
        data-state="${state.isSaved ? 'saved' : 'idle'}"
        aria-label="${state.isSaved ? t.saved : t.saveArticle}"
        title="${state.isSaved ? t.saved : t.saveArticle}"
      >
        ${state.isSaved ? SAVE_ICON_FILLED_SVG : SAVE_ICON_SVG}
      </button>
      <a
        href="${accountUrl}"
        class="milla-auth-btn milla-auth-btn-icon milla-auth-btn-settings"
        aria-label="${t.accountSettings}"
        title="${t.accountSettings}"
      >
        ${SETTINGS_ICON_SVG}
      </a>
    </div>
  `;
}

const SCROLL_TOP_ICON_SVG = `<svg class="milla-auth-scroll-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <path d="M18 15l-6-6-6 6"/>
</svg>`;

const SAVE_ICON_SVG = `<svg class="milla-auth-action-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
</svg>`;

const SAVE_ICON_FILLED_SVG = `<svg class="milla-auth-action-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
</svg>`;

const SETTINGS_ICON_SVG = `<svg class="milla-auth-action-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="3"/>
  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
</svg>`;

function renderBrandLogo() {
  const visible = barState.scrolledPastThreshold;
  const t = getTranslations();
  const brand = detectBrand();
  const isMtlBlog = brand === 'mtlblog';
  const logoSvg = isMtlBlog ? MTLBLOG_LOGO_SVG : NARCITY_LOGO_SVG;
  const homeUrl = isMtlBlog ? 'https://www.mtlblog.com' : 'https://www.narcity.com';
  const brandLabel = isMtlBlog ? 'MTL Blog' : 'Narcity';
  // Brand styling is now handled via data-brand attribute on the bar container

  return `
    <div class="milla-auth-brand-logo-wrap" data-visible="${visible}">
      <a href="${homeUrl}" aria-label="${brandLabel}">
        ${logoSvg}
      </a>
    </div>
    <button class="milla-auth-btn milla-auth-btn-scroll-top" data-visible="${visible}" data-action="scroll-top" aria-label="${t.scrollToTop}">
      ${SCROLL_TOP_ICON_SVG}
    </button>
    <div class="milla-auth-divider" data-visible="${visible}"></div>
  `;
}

function renderPrivacyButton() {
  const label = privacyControls.getLauncherLabel();
  return label ? `<button type="button" class="milla-auth-btn milla-auth-btn-icon milla-auth-btn-privacy" data-action="privacy" aria-haspopup="dialog" aria-label="${label}" title="${label}">${PRIVACY_ICON_SVG}</button>` : '';
}

function syncPrivacyButton() {
  if (!barState.container) return;
  if (Boolean(barState.container.querySelector('[data-action="privacy"]')) !== Boolean(privacyControls.getLauncherLabel())) render();
}

function render() {
  if (!barState.container) return;

  let contentHtml = '';
  if (barState.status === 'loading') {
    contentHtml = renderLoading() + renderPrivacyButton();
  } else if (barState.status === 'authenticated' && barState.user) {
    contentHtml = renderLoggedIn(barState.user, barState);
  } else {
    contentHtml = renderLoggedOut();
  }

  const html = `
    ${renderBrandLogo()}
    <div class="milla-auth-content">${contentHtml}</div>
  `;

  barState.container.innerHTML = html;
}

function handleClick(event) {
  const target = event.target.closest('[data-action]');
  if (!target) return;

  const action = target.dataset.action;
  if (action === 'login' && barState.auth?.login) {
    barState.auth.login();
  } else if (action === 'privacy') {
    barState.auth?.identity?.openPrivacyChoices();
  } else if (action === 'save') {
    handleSaveAction();
  } else if (action === 'scroll-top') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

async function syncFavouriteState() {
  const auth = barState.auth;
  if (!auth || typeof auth.isFavourite !== 'function') return;
  const article = buildArticlePayloadForHost();
  if (!article || !article.id) return;
  try {
    const result = await auth.isFavourite(article.id);
    barState.isSaved = !!(result && result.isSaved);
    render();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug?.('[milla-auth-bar] failed to fetch favourite state', err);
  }
}

async function handleSaveAction() {
  const auth = barState.auth;
  if (!auth) return;
  const article = buildArticlePayloadForHost();
  if (!article || !article.id) return;

  const interactions = typeof window !== 'undefined' ? window.MillaInteractions : null;

  barState.saveLoading = true;
  render();

  try {
    if (interactions && typeof interactions.toggleFavourite === 'function') {
      await interactions.toggleFavourite(article, barState.isSaved);
      barState.isSaved = !barState.isSaved;
    } else if (typeof auth.addFavourite === 'function' && typeof auth.removeFavourite === 'function') {
      if (barState.isSaved) {
        await auth.removeFavourite(article.id);
        barState.isSaved = false;
      } else {
        await auth.addFavourite(article);
        barState.isSaved = true;
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn?.('[milla-auth-bar] failed to toggle favourite', err);
  } finally {
    barState.saveLoading = false;
    render();
  }
}

async function handleSummarizeAction() {
  const auth = barState.auth;
  if (!auth) return;
  const text = buildArticleTextForHost();
  if (!text) return;

  const interactions = typeof window !== 'undefined' ? window.MillaInteractions : null;

  barState.summarizeLoading = true;
  barState.summaryText = '';
  render();

  try {
    let summary = null;
    if (interactions && typeof interactions.summarize === 'function') {
      summary = await interactions.summarize(text);
    } else if (typeof auth.summarize === 'function') {
      summary = await auth.summarize(text);
    }
    if (summary) {
      barState.summaryText = summary;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn?.('[milla-auth-bar] failed to summarize article', err);
  } finally {
    barState.summarizeLoading = false;
    render();
  }
}

function syncProfile() {
  const auth = barState.auth;
  if (!auth) return;

  // OPTIMIZATION: If we've confirmed user is logged out, skip fetch entirely
  // This speeds up UI for logged-out users by avoiding failed API calls
  if (typeof auth.isKnownLoggedOut === 'function' && auth.isKnownLoggedOut()) {
    clearProfile();
    return;
  }

  // Cached identity is a paint hint; entitlements must be revalidated even
  // after a reload (sessionStorage survives hard refreshes).
  const cachedProfile = getCachedProfile();
  if (cachedProfile) {
    barState.user = { ...cachedProfile, proStatus: null };
    barState.status = 'authenticated';
    render();
  }
  return refreshProfile();
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth <= 768;
}

function setupScrollListener() {
  if (typeof window === 'undefined') return;

  // Remove existing handler if any
  if (barState.scrollHandler) {
    window.removeEventListener('scroll', barState.scrollHandler);
  }

  let ticking = false;
  barState.scrollHandler = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const scrollY = window.scrollY || window.pageYOffset;
        const shouldShow = isMobileViewport() && scrollY > MOBILE_SCROLL_THRESHOLD;

        if (shouldShow !== barState.scrolledPastThreshold) {
          barState.scrolledPastThreshold = shouldShow;
          // Update element visibility without full re-render for performance
          const logoWrap = barState.container?.querySelector('.milla-auth-brand-logo-wrap');
          const scrollTopBtn = barState.container?.querySelector('.milla-auth-btn-scroll-top');
          const divider = barState.container?.querySelector('.milla-auth-divider');
          if (logoWrap) logoWrap.dataset.visible = shouldShow;
          if (scrollTopBtn) scrollTopBtn.dataset.visible = shouldShow;
          if (divider) divider.dataset.visible = shouldShow;
          // Update container scrolled state for mobile styling
          if (barState.container) barState.container.dataset.scrolled = shouldShow;
        }
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', barState.scrollHandler, { passive: true });

  // Initial check
  barState.scrollHandler();
}

function removeScrollListener() {
  if (typeof window === 'undefined') return;
  if (barState.scrollHandler) {
    window.removeEventListener('scroll', barState.scrollHandler);
    barState.scrollHandler = null;
  }
}

function clearProfile() {
  // In-flight responses from an old session must not restore its user/cache.
  barState.profileVersion += 1;
  barState.profileRequest = null;
  barState.profileAwaitingToken = false;
  barState.profileToken = null;
  barState.user = null;
  barState.status = 'unauthenticated';
  clearCachedProfile();
  render();
}

function handleAuthReady({ authenticated }) {
  if (!authenticated) clearProfile();
}

function handleToken(token) {
  if (token === barState.profileToken) {
    refreshProfile();
    return;
  }
  // The initial profile lookup is already waiting for this session token.
  // Let it continue instead of issuing a duplicate /me request at startup.
  if (barState.profileRequest && barState.profileAwaitingToken) {
    barState.profileAwaitingToken = false;
    barState.profileToken = token;
    return;
  }
  clearProfile();
  barState.profileToken = token;
  refreshProfile();
}

function handleReturn() {
  if (document.visibilityState !== 'hidden') refreshProfile();
}

function handlePageShow(event) {
  if (event.persisted) handleReturn();
}

function setupEventListeners() {
  const auth = barState.auth;
  if (!auth) return;
  auth.on('identity:ready', syncPrivacyButton);
  auth.on('auth:ready', handleAuthReady);
  auth.on('token', handleToken);
  auth.on('logout', clearProfile);
  auth.on('token:error', clearProfile);
  window.addEventListener('focus', handleReturn);
  window.addEventListener('pageshow', handlePageShow);
  document.addEventListener('visibilitychange', handleReturn);
}

function refreshProfile() {
  const auth = barState.auth;
  if (!auth || !barState.mounted) return;
  if (auth.isKnownLoggedOut?.()) {
    clearProfile();
    return;
  }
  // Focus and visibility events often arrive together. Share their lookup.
  if (barState.profileRequest) return barState.profileRequest;

  const version = barState.profileVersion;
  barState.profileAwaitingToken = typeof auth.isAuthenticated === 'function' && !auth.isAuthenticated();
  const isCurrent = () => barState.mounted && barState.profileVersion === version;
  if (!barState.user) {
    barState.status = 'loading';
    render();
  }
  const loadingTimeout = setTimeout(() => {
    if (isCurrent() && barState.status === 'loading') {
      barState.status = 'unauthenticated';
      render();
    }
  }, LOADING_TIMEOUT_MS);

  barState.profileRequest = (async () => {
    try {
      const profile = await fetchProfile(auth);
      if (!isCurrent()) return;
      barState.user = profile;
      barState.status = 'authenticated';
      setCachedProfile(profile);
      syncFavouriteState();
      render();
    } catch (err) {
      if (!isCurrent()) return;
      clearCachedProfile();
      // An unavailable member service is not a logout, but cannot confirm Pro.
      barState.user = err.status === 401 || err.status === 404
        ? null
        : barState.user && { ...barState.user, proStatus: null };
      barState.status = barState.user ? 'authenticated' : 'unauthenticated';
      render();
    } finally {
      clearTimeout(loadingTimeout);
      if (isCurrent()) {
        barState.profileRequest = null;
        barState.profileAwaitingToken = false;
      }
    }
  })();
  return barState.profileRequest;
}

function mount(auth) {
  if (typeof document === 'undefined') return;
  if (barState.mounted) return;

  barState.auth = auth || window.MillaAuth;
  if (!barState.auth) {
    console.warn('[milla-auth-bar] MillaAuth SDK not found');
    return;
  }

  injectStyles();

  const container = document.createElement('div');
  container.id = 'milla-auth-bar';
  container.dataset.brand = detectBrand();
  document.body.appendChild(container);

  barState.container = container;
  barState.mounted = true;

  container.addEventListener('click', handleClick);

  setupEventListeners();
  setupScrollListener();
  syncProfile();
}

function unmount() {
  if (!barState.mounted || !barState.container) return;
  barState.auth?.off('identity:ready', syncPrivacyButton);
  barState.auth?.off('auth:ready', handleAuthReady);
  barState.auth?.off('token', handleToken);
  barState.auth?.off('logout', clearProfile);
  barState.auth?.off('token:error', clearProfile);
  window.removeEventListener('focus', handleReturn);
  window.removeEventListener('pageshow', handlePageShow);
  document.removeEventListener('visibilitychange', handleReturn);
  barState.profileVersion += 1;
  barState.profileRequest = null;
  barState.profileAwaitingToken = false;
  barState.profileToken = null;

  barState.container.removeEventListener('click', handleClick);
  removeScrollListener();
  barState.container.remove();

  const styles = document.getElementById('milla-auth-bar-styles');
  if (styles) styles.remove();

  barState.mounted = false;
  barState.container = null;
  barState.user = null;
  barState.status = 'loading';
  barState.scrolledPastThreshold = false;
}

module.exports = {
  mount,
  unmount,
  refreshProfile
};
